import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Registration } from '../types';
import { ROLE_DASHBOARD_MAP } from '../constants/roles';
import { WarningIcon } from './Icons';
import { uploadImageToCdn } from '../lib/uploadImage';
import avatar1 from '../assets/avatar1.png';
import avatar2 from '../assets/avatar2.png';
import avatar3 from '../assets/avatar3.png';
import avatar4 from '../assets/avatar4.png';
import avatar5 from '../assets/avatar5.png';

const API_URL = BACKEND_URL;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, token, updateAvatar, logout } = useAuth();

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      logout();
      navigate('/');
    } catch (err: any) {
      setUpdateMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete account' });
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [updateMessage, setUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const avatars = [avatar1, avatar2, avatar3, avatar4, avatar5];

  // Check if current user avatar is a custom uploaded photo (base64 or not in predefined avatars)
  const isCustomAvatar = (avatar: string | undefined) => {
    if (!avatar) return false;
    return !avatars.includes(avatar) && (avatar.startsWith('data:image') || avatar.startsWith('http'));
  };

  // Initialize selectedAvatar when modal opens or when user changes
  const handleOpenAvatarPicker = () => {
    setSelectedAvatar(user?.avatar || avatars[0]);
    // If user has a custom avatar, set it as uploadedImage so it shows in the grid
    if (user?.avatar && isCustomAvatar(user.avatar)) {
      setUploadedImage(user.avatar);
    } else {
      setUploadedImage(null);
    }
    setShowAvatarPicker(true);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const uploadedUrl = await uploadImageToCdn(file, token, 'avatars');
        setUploadedImage(uploadedUrl);
        setSelectedAvatar(uploadedUrl);
      } catch (error: any) {
        alert(error?.message || 'Failed to upload image');
      }
    }
  };

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/registrations/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRegistrations(response.data);
      } catch (error) {
        console.error('Failed to fetch registrations', error);
      }
    };
    fetchRegistrations();
  }, [token]);

  const handleChangeAvatar = async () => {
    try {
      if (!selectedAvatar) {
        console.error('No avatar selected');
        alert('Please select an avatar');
        return;
      }
      
      console.log('Attempting to update avatar:', selectedAvatar);
      const response = await axios.put(
        `${API_URL}/api/auth/update-avatar`,
        { avatar: selectedAvatar },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Avatar update response:', response.data);
      updateAvatar(selectedAvatar);
      console.log('Avatar updated in context');
      setShowAvatarPicker(false);
    } catch (error: any) {
      console.error('Failed to update avatar', error.response?.data || error.message);
      alert('Failed to update avatar: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async () => {
    try {
      // Validate form
      if (!editFormData.username.trim()) {
        setUpdateMessage({ type: 'error', text: 'Username cannot be empty' });
        return;
      }
      if (!editFormData.email.trim()) {
        setUpdateMessage({ type: 'error', text: 'Email cannot be empty' });
        return;
      }

      // If password is being changed, validate it
      if (editFormData.newPassword) {
        if (!editFormData.currentPassword) {
          setUpdateMessage({ type: 'error', text: 'Current password is required to change password' });
          return;
        }
        if (editFormData.newPassword !== editFormData.confirmPassword) {
          setUpdateMessage({ type: 'error', text: 'New passwords do not match' });
          return;
        }
        if (editFormData.newPassword.length < 6) {
          setUpdateMessage({ type: 'error', text: 'Password must be at least 6 characters' });
          return;
        }
      }

      const updateData: any = {
        username: editFormData.username,
        email: editFormData.email,
      };

      if (editFormData.newPassword) {
        updateData.currentPassword = editFormData.currentPassword;
        updateData.newPassword = editFormData.newPassword;
      }

      const response = await axios.put(
        `${API_URL}/api/auth/update-profile`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUpdateMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      // If username changed, update the token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      setIsEditMode(false);
      // Reset form
      setEditFormData({
        username: response.data.user.username || editFormData.username,
        email: response.data.user.email || editFormData.email,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setUpdateMessage(null), 3000);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || 'Failed to update profile';
      setUpdateMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleBackToDashboard = () => {
    if (user?.role) {
      const normalizedRole = user.role.toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
      const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole];
      navigate(dashboardPath || '/');
    } else {
      navigate('/');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="profile-page">
      {/* Back Button */}
      <motion.button
        onClick={handleBackToDashboard}
        className="back-button"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        ← BACK TO DASHBOARD
      </motion.button>

      <div className="profile-techfest-container">
        {/* Central Panel with Avatar */}
        <div className="profile-central-panel">
          <div className="profile-avatar-container">
            <img
              src={user?.avatar || avatars[0]}
              alt="Avatar"
              className="profile-main-avatar"
            />
            <button onClick={handleOpenAvatarPicker} className="change-avatar-button">
              CHANGE AVATAR
            </button>
          </div>

          {/* User Info Panels */}
          <div className="profile-info-panels">
            {!isEditMode ? (
              <>
                <div className="info-panel">
                  <span className="info-label">USERNAME</span>
                  <span className="info-value">{user?.username}</span>
                </div>
                <div className="info-panel">
                  <span className="info-label">EMAIL</span>
                  <span className="info-value">{user?.email}</span>
                </div>
                <div className="info-panel">
                  <span className="info-label">ROLE</span>
                  <span className="info-value">{user?.role.replace('_', ' ').toUpperCase()}</span>
                </div>
                <div className="info-panel">
                  <button
                    className="cyber-button"
                    onClick={() => {
                      setEditFormData({
                        username: user?.username || '',
                        email: user?.email || '',
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                      setIsEditMode(true);
                    }}
                  >
                    EDIT PROFILE
                  </button>
                </div>
                <div className="info-panel">
                  <button
                    className="cyber-button"
                    onClick={() => {
                      logout();
                      window.location.href = '/';
                    }}
                  >
                    LOGOUT
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="info-panel">
                  <span className="info-label">USERNAME</span>
                  <input
                    type="text"
                    name="username"
                    value={editFormData.username}
                    onChange={handleEditFormChange}
                    className="edit-input"
                    placeholder="Enter username"
                  />
                </div>
                <div className="info-panel">
                  <span className="info-label">EMAIL</span>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditFormChange}
                    className="edit-input"
                    placeholder="Enter email"
                  />
                </div>
                <div className="info-panel">
                  <span className="info-label">CURRENT PASSWORD</span>
                  <input
                    type="password"
                    name="currentPassword"
                    value={editFormData.currentPassword}
                    onChange={handleEditFormChange}
                    className="edit-input"
                    placeholder="Enter current password"
                  />
                </div>
                <div className="info-panel">
                  <span className="info-label">NEW PASSWORD</span>
                  <input
                    type="password"
                    name="newPassword"
                    value={editFormData.newPassword}
                    onChange={handleEditFormChange}
                    className="edit-input"
                    placeholder="Enter new password (leave blank to keep current)"
                  />
                </div>
                <div className="info-panel">
                  <span className="info-label">CONFIRM PASSWORD</span>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={editFormData.confirmPassword}
                    onChange={handleEditFormChange}
                    className="edit-input"
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="info-panel">
                  <button
                    className="cyber-button"
                    onClick={() => setIsEditMode(false)}
                  >
                    CANCEL
                  </button>
                </div>
                <div className="info-panel" style={{ marginTop: '12px' }}>
                  <button
                    className="cyber-button"
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      background: 'linear-gradient(135deg, #dc3545, #a71d2a)',
                      border: '1px solid #dc3545',
                      color: '#fff',
                      width: '100%'
                    }}
                  >
                    DELETE ACCOUNT
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Side Cards */}
        <div className="profile-right-cards">
          {isEditMode && updateMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`update-message ${updateMessage.type}`}
            >
              {updateMessage.text}
            </motion.div>
          )}

          <motion.div whileHover={{ scale: 1.05 }} className="profile-event-card">
            <h3 className="card-header">EVENTS</h3>
            <div className="card-image-placeholder">
              <div className="glitch-effect">EVENTS</div>
            </div>
            <p className="card-count">Enrolled: {registrations.filter(r => true).length}</p>
          </motion.div>

          <motion.div whileHover={{ scale: 1.05 }} className="profile-event-card">
            <h3 className="card-header">WORKSHOPS</h3>
            <div className="card-image-placeholder">
              <div className="glitch-effect">TECH LABS</div>
            </div>
            <p className="card-count">No workshops registered</p>
          </motion.div>

          {isEditMode && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="update-profile-button"
              onClick={handleUpdateProfile}
            >
              UPDATE PROFILE
            </motion.button>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '400px', textAlign: 'center' }}
          >
            <h3 className="modal-title" style={{ color: '#dc3545' }}><WarningIcon size={18} color="#dc3545" /> DELETE ACCOUNT</h3>
            <p style={{ color: '#ccc', marginBottom: '8px', fontSize: '15px' }}>
              Are you sure you want to delete your account?
            </p>
            <p style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '24px' }}>
              This action is <strong>permanent</strong> and cannot be undone. All your data will be erased.
            </p>
            <div className="modal-buttons">
              <button
                className="cyber-button"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                style={{
                  background: 'linear-gradient(135deg, #dc3545, #a71d2a)',
                  border: '1px solid #dc3545',
                  opacity: deleteLoading ? 0.6 : 1,
                  cursor: deleteLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {deleteLoading ? 'DELETING...' : 'YES, DELETE'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="cyber-button-secondary"
                disabled={deleteLoading}
              >
                CANCEL
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {showAvatarPicker && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="modal-overlay" onClick={() => setShowAvatarPicker(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">SELECT AVATAR</h3>
            
            {/* Upload Section */}
            <div className="upload-section" style={{ marginBottom: '20px', textAlign: 'center' }}>
              <label htmlFor="avatar-upload" className="cyber-button" style={{ cursor: 'pointer', display: 'inline-block' }}>
                UPLOAD PHOTO
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              {uploadedImage && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#00ff9d' }}>
                  ✓ Custom photo uploaded
                </div>
              )}
            </div>

            <div className="avatar-grid">
              {avatars.map((avatar, index) => (
                <img
                  key={index}
                  src={avatar}
                  alt={`Avatar ${index + 1}`}
                  className={`avatar-option ${selectedAvatar === avatar ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedAvatar(avatar);
                    setUploadedImage(null);
                  }}
                />
              ))}
              {uploadedImage && (
                <img
                  src={uploadedImage}
                  alt="Uploaded"
                  className={`avatar-option ${selectedAvatar === uploadedImage ? 'selected' : ''}`}
                  onClick={() => setSelectedAvatar(uploadedImage)}
                  style={{ border: '2px solid #00ff9d' }}
                />
              )}
            </div>
            <div className="modal-buttons">
              <button onClick={handleChangeAvatar} className="cyber-button">
                CONFIRM
              </button>
              <button onClick={() => setShowAvatarPicker(false)} className="cyber-button-secondary">
                CANCEL
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ProfilePage;
